import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Sun, Heart, Wind, CloudRain, Moon, Check } from "lucide-react";

const MOODS = [
  { key: "open", label: "Open", icon: Sun },
  { key: "tender", label: "Tender", icon: Heart },
  { key: "restless", label: "Restless", icon: Wind },
  { key: "heavy", label: "Heavy", icon: CloudRain },
  { key: "quiet", label: "Quiet", icon: Moon },
];

export default function MoodCheckin() {
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [reflection, setReflection] = useState(null);

  const submit = async () => {
    if (!selected || loading) return;
    setLoading(true);
    try {
      const res = await api.post("/mood", { mood: selected, note: note || null });
      setReflection(res.data.reflection);
    } catch (e) {
      setReflection("Thank you for checking in. Noticing how you feel is already part of the work.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setSelected(null);
    setNote("");
    setReflection(null);
  };

  return (
    <div
      data-testid="mood-checkin"
      className="relative h-full border border-[#F4F1EC]/12 bg-[#F4F1EC]/[0.03] p-8 md:p-10 flex flex-col overflow-hidden"
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#F4F1EC]/55 font-body">
          Mood check-in
        </span>
      </div>

      <AnimatePresence mode="wait">
        {reflection ? (
          <motion.div
            key="reflection"
            data-testid="mood-reflection"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="flex-1 flex flex-col justify-center py-6"
          >
            <span className="h-9 w-9 inline-flex items-center justify-center bg-gradient-to-br from-[#FFB7C6] to-[#A8E6FF] text-[#070608] mb-5">
              <Check size={16} strokeWidth={2.2} />
            </span>
            <p className="font-display text-[24px] md:text-[26px] leading-[1.3] italic font-light text-[#F4F1EC]">
              {reflection}
            </p>
            <button
              data-testid="mood-reset-button"
              onClick={reset}
              className="self-start mt-7 font-body text-[11px] uppercase tracking-[0.24em] text-[#F4F1EC]/60 hover:text-[#FFB7C6] transition-colors"
            >
              Check in again
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="form"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 flex flex-col"
          >
            <p className="mt-3 font-display text-[22px] leading-tight text-[#F4F1EC]/90 italic font-light">
              How are you arriving today?
            </p>

            <div className="mt-6 grid grid-cols-5 gap-2">
              {MOODS.map((m) => {
                const Icon = m.icon;
                const active = selected === m.key;
                return (
                  <button
                    key={m.key}
                    data-testid={`mood-option-${m.key}`}
                    onClick={() => setSelected(m.key)}
                    className={`flex flex-col items-center gap-2 py-3 border transition-colors ${
                      active
                        ? "border-[#FFB7C6] bg-[#FFB7C6]/10 text-[#FFB7C6]"
                        : "border-[#F4F1EC]/12 text-[#F4F1EC]/65 hover:border-[#F4F1EC]/30"
                    }`}
                  >
                    <Icon size={18} strokeWidth={1.5} />
                    <span className="text-[9px] uppercase tracking-[0.14em] font-body">
                      {m.label}
                    </span>
                  </button>
                );
              })}
            </div>

            <textarea
              data-testid="mood-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="A line, if you'd like. What's moving through you?"
              rows={2}
              className="mt-5 w-full bg-transparent border-b border-[#F4F1EC]/20 text-[#F4F1EC] placeholder:text-[#F4F1EC]/35 font-body text-sm py-2 outline-none resize-none focus:border-[#FFB7C6] transition-colors"
            />

            <button
              data-testid="mood-submit-button"
              onClick={submit}
              disabled={!selected || loading}
              className="self-start mt-6 h-11 px-7 font-body text-[11px] uppercase tracking-[0.24em] bg-[#F4F1EC] text-[#070608] hover:bg-white disabled:opacity-40 transition-colors"
            >
              {loading ? "…" : "Check in"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
